import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Mail, X, Copy, Check } from 'lucide-react';

const CRMNotifications = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingEmail, setGeneratingEmail] = useState(null);
  const [emailDrafts, setEmailDrafts] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen) {
      fetchFollowups();
    }
  }, [isOpen]);

  // Poll for new follow-ups every 5 minutes
  useEffect(() => {
    fetchFollowups();
    const interval = setInterval(fetchFollowups, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchFollowups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/crm/follow-ups/due`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFollowups(data.followups || []);
      }
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
    }
  };

  const generateEmail = async (contactId) => {
    setGeneratingEmail(contactId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/crm/generate-followup-email/${contactId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const draft = await response.json();
        setEmailDrafts(prev => ({ ...prev, [contactId]: draft }));
      } else {
        alert('Failed to generate email draft');
      }
    } catch (error) {
      console.error('Error generating email:', error);
      alert('Error generating email draft');
    } finally {
      setGeneratingEmail(null);
    }
  };

  const openInGmail = (draft) => {
    const subject = encodeURIComponent(draft.subject);
    const body = encodeURIComponent(draft.body);
    const to = encodeURIComponent(draft.to_email);
    
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${subject}&body=${body}`;
    window.open(gmailUrl, '_blank');
  };

  const copyToClipboard = (draft, contactId) => {
    const text = `To: ${draft.to_email}\nSubject: ${draft.subject}\n\n${draft.body}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(contactId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const formatDaysOverdue = (days) => {
    if (days === 0) return 'Due today';
    if (days === 1) return '1 day overdue';
    return `${days} days overdue`;
  };

  return (
    <div className="relative">
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative text-blue-600 hover:text-blue-800 hover:bg-white/40 rounded-xl p-2 transition-colors"
      >
        <Bell className="h-5 w-5" />
        {followups.length > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-xs text-white shadow-lg font-bold">
            {followups.length}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[600px] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Bell size={18} />
                CRM Follow-ups
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-slate-500 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1">
              {followups.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell size={48} className="mx-auto mb-3 opacity-30" />
                  <p className="font-medium">All caught up!</p>
                  <p className="text-sm mt-1">No follow-ups due</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {followups.map((followup) => {
                    const draft = emailDrafts[followup.id];
                    const isGenerating = generatingEmail === followup.id;
                    const isCopied = copiedId === followup.id;

                    return (
                      <div key={followup.id} className="p-4 hover:bg-gray-50 transition-colors">
                        {/* Contact Info */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <button
                              onClick={() => {
                                navigate(`/crm/contacts/${followup.id}`);
                                setIsOpen(false);
                              }}
                              className="font-semibold text-gray-900 hover:text-blue-600 text-left"
                            >
                              {followup.first_name} {followup.last_name}
                            </button>
                            {followup.company && (
                              <p className="text-sm text-gray-600">{followup.company}</p>
                            )}
                            <p className="text-xs text-orange-600 font-medium mt-1">
                              {formatDaysOverdue(followup.days_overdue)}
                            </p>
                          </div>
                          <button onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              await fetch(`${backendUrl}/api/crm/follow-ups/dismiss/${followup.id}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
                              setFollowups(prev => prev.filter(f => f.id !== followup.id));
                            } catch {}
                          }} className="text-xs text-slate-500 hover:text-red-500 px-2 py-1" title="Dismiss">
                            <X size={14} />
                          </button>
                        </div>

                        {/* Email Draft */}
                        {!draft && !isGenerating && (
                          <button
                            onClick={() => generateEmail(followup.id)}
                            className="w-full mt-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                          >
                            <Mail size={16} />
                            Generate Email Draft
                          </button>
                        )}

                        {isGenerating && (
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg text-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="text-sm text-gray-600 mt-2">Generating...</p>
                          </div>
                        )}

                        {draft && (
                          <div className="mt-3 p-3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                            <p className="text-xs font-semibold text-gray-700 mb-1">Subject:</p>
                            <p className="text-sm font-medium text-gray-900 mb-3">{draft.subject}</p>
                            
                            <p className="text-xs font-semibold text-gray-700 mb-1">Message:</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3">{draft.body}</p>
                            
                            <div className="flex gap-2">
                              <button
                                onClick={() => openInGmail(draft)}
                                className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                              >
                                <Mail size={16} />
                                Open in Gmail
                              </button>
                              <button
                                onClick={() => copyToClipboard(draft, followup.id)}
                                className="px-3 py-2 bg-slate-200 text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
                                title="Copy to clipboard"
                              >
                                {isCopied ? <Check size={16} /> : <Copy size={16} />}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {followups.length > 0 && (
              <div className="p-3 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => {
                    navigate('/crm/contacts');
                    setIsOpen(false);
                  }}
                  className="w-full text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View All Contacts →
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CRMNotifications;
